import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Sidebar from '../components/Sidebar';
import TagSelector from '../components/TagSelector';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import {
  fetchVerifiedUsers,
  updateUserRoles,
  inviteEmployee,
  setUserDeactivated,
  isUserDeactivated,
  type VerifiedUser,
} from '../api/users';
import { ROLE_OPTIONS } from '../api/users';
import { fetchAllTasks, type TaskRow } from '../api/tasks';
import { TAG_PILL_COLORS, ROLE_LABELS } from '../constants/taskTags';
import { formatTaskDate, isTaskOverdue } from '../utils/formatTaskDate';
import { TaskStatusPills } from '../components/TaskStatusPills';
import { SortableTh, StaticTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import './Page.css';
import './Settings.css';
import './Employees.css';

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

function getUserRoles(u: { roles?: string[] }): string[] {
  return Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [];
}

function UserTasksTable({ tasks }: { tasks: TaskRow[] }) {
  const navigate = useNavigate();
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(tasks, sortKey, sortDir, (t, key) => {
        switch (key) {
          case 'name':
            return t.name ?? '';
          case 'status':
            return `${t.status ?? ''}\t${isTaskOverdue(t) ? '0' : '1'}`;
          case 'priority':
            return t.priority ?? '';
          case 'tags':
            return (t.tags ?? []).join(', ');
          case 'company':
            return t.client_company ?? '';
          case 'due':
            return t.due_date ?? '';
          default:
            return '';
        }
      }),
    [tasks, sortKey, sortDir]
  );
  return (
    <table className="employees-tasks-table">
      <thead>
        <tr>
          <SortableTh label="Task" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          <SortableTh label="Priority" columnKey="priority" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          <SortableTh label="Roles" columnKey="tags" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          <SortableTh label="Company" columnKey="company" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          <SortableTh label="Due date" columnKey="due" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 ? (
          <tr>
            <td colSpan={6}>No tasks assigned.</td>
          </tr>
        ) : (
          sorted.map((t) => (
            <tr
              key={t.id}
              className="employees-tasks-table-row"
              onClick={() => navigate(`/tasks/${t.id}`)}
            >
              <td data-label="Task">{t.name}</td>
              <td data-label="Status">
                <TaskStatusPills task={t} pillClassName="employees-task-pill" />
              </td>
              <td data-label="Priority">
                {t.priority ? (
                  <span
                    className="employees-task-pill"
                    style={{ backgroundColor: PRIORITY_PILL_COLORS[t.priority] ?? '#e5e7eb' }}
                  >
                    {t.priority}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td data-label="Roles">
                <div className="employees-task-tags">
                  {(t.tags?.length ? t.tags : []).map((tag) => (
                    <span
                      key={tag}
                      className="employees-task-pill"
                      style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#e5e7eb' }}
                    >
                      {ROLE_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {(!t.tags || t.tags.length === 0) && '—'}
                </div>
              </td>
              <td data-label="Company">{t.client_company ?? '—'}</td>
              <td data-label="Due date">{formatTaskDate(t.due_date)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState<VerifiedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnSelfAdminRevoke, setWarnSelfAdminRevoke] = useState(false);
  const [pendingRoles, setPendingRoles] = useState<string[] | null>(null);
  const [allTasks, setAllTasks] = useState<TaskRow[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const [addEmployeeModalOpen, setAddEmployeeModalOpen] = useState(false);
  const [addEmployeeEmail, setAddEmployeeEmail] = useState('');
  const [addEmployeeSending, setAddEmployeeSending] = useState(false);
  const [addEmployeeError, setAddEmployeeError] = useState('');
  const [addEmployeeSuccess, setAddEmployeeSuccess] = useState('');

  const [deactivateTarget, setDeactivateTarget] = useState<VerifiedUser | null>(null);
  const [deactivateError, setDeactivateError] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('admin');

  const searchLower = searchQuery.trim().toLowerCase();
  const filteredUsers = useMemo(() => {
    if (!searchLower) return users;
    return users.filter((u) => {
      const username = (u.username ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      const roleLabels = getUserRoles(u).map((r) => (ROLE_LABELS[r] ?? r).toLowerCase());
      const roleKeys = getUserRoles(u).map((r) => r.toLowerCase());
      const status = isUserDeactivated(u) ? 'deactivated' : 'active';
      return (
        username.includes(searchLower) ||
        email.includes(searchLower) ||
        roleLabels.some((l) => l.includes(searchLower)) ||
        roleKeys.some((k) => k.includes(searchLower)) ||
        status.includes(searchLower)
      );
    });
  }, [users, searchLower]);

  const employeeSort = useTableSortState();
  const sortedFilteredUsers = useMemo(
    () =>
      sortRows(filteredUsers, employeeSort.sortKey, employeeSort.sortDir, (u, key) => {
        switch (key) {
          case 'username':
            return u.username ?? '';
          case 'email':
            return u.email ?? '';
          case 'roles':
            return getUserRoles(u)
              .map((r) => ROLE_LABELS[r] ?? r)
              .join(', ');
          case 'status':
            return isUserDeactivated(u) ? 'Deactivated' : 'Active';
          default:
            return '';
        }
      }),
    [filteredUsers, employeeSort.sortKey, employeeSort.sortDir]
  );

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }
    let cancelled = false;
    fetchVerifiedUsers({ includeDeactivated: true })
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin || !user?.id) {
      setAllTasks([]);
      return;
    }
    let cancelled = false;
    fetchAllTasks(user.id)
      .then((list) => {
        if (!cancelled) setAllTasks(list);
      })
      .catch(() => {
        if (!cancelled) setAllTasks([]);
      });
    return () => { cancelled = true; };
  }, [isAdmin, user?.id]);

  const tasksForUser = (userId: number) =>
    allTasks.filter(
      (t) =>
        (t.assignees ?? []).some((a) => a.user_id === userId) ||
        t.assigned_to_user_id === userId
    );

  const toggleTasks = (userId: number) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const openEdit = (u: VerifiedUser) => {
    setEditUserId(u.id);
    setEditRoles(getUserRoles(u));
    setSaveError('');
  };

  const closeEdit = () => {
    setEditUserId(null);
    setSaveError('');
  };

  const handleRolesChange = (newRoles: string[]) => {
    const removingSelfAdmin =
      user != null &&
      editUserId === user.id &&
      editRoles.includes('admin') &&
      !newRoles.includes('admin');

    if (removingSelfAdmin) {
      setPendingRoles(newRoles);
      setWarnSelfAdminRevoke(true);
    } else {
      setEditRoles(newRoles);
    }
  };

  const confirmAdminRevoke = () => {
    if (pendingRoles !== null) setEditRoles(pendingRoles);
    setPendingRoles(null);
    setWarnSelfAdminRevoke(false);
  };

  const cancelAdminRevoke = () => {
    setPendingRoles(null);
    setWarnSelfAdminRevoke(false);
  };

  const handleSaveRoles = async () => {
    if (!user || editUserId == null) return;
    if (editRoles.length === 0) {
      setSaveError('Select at least one role.');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      await updateUserRoles(editUserId, editRoles, user.id);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editUserId ? { ...u, roles: editRoles } : u
        )
      );
      closeEdit();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update roles.');
    } finally {
      setSaving(false);
    }
  };

  const openDeactivate = (u: VerifiedUser) => {
    setDeactivateTarget(u);
    setDeactivateError('');
  };

  const closeDeactivate = () => {
    if (deactivating) return;
    setDeactivateTarget(null);
    setDeactivateError('');
  };

  const handleConfirmDeactivate = async () => {
    if (!user || !deactivateTarget) return;
    setDeactivateError('');
    setDeactivating(true);
    try {
      const { deactivated_at } = await setUserDeactivated(deactivateTarget.id, true, user.id);
      setUsers((prev) =>
        prev.map((u) => (u.id === deactivateTarget.id ? { ...u, deactivated_at } : u))
      );
      setDeactivateTarget(null);
    } catch (err) {
      setDeactivateError(err instanceof Error ? err.message : 'Failed to deactivate account.');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (target: VerifiedUser) => {
    if (!user) return;
    setDeactivateError('');
    try {
      const { deactivated_at } = await setUserDeactivated(target.id, false, user.id);
      setUsers((prev) =>
        prev.map((u) => (u.id === target.id ? { ...u, deactivated_at } : u))
      );
    } catch (err) {
      setDeactivateError(err instanceof Error ? err.message : 'Failed to reactivate account.');
    }
  };

  const handleAddEmployeeOpen = () => {
    setAddEmployeeEmail('');
    setAddEmployeeError('');
    setAddEmployeeSuccess('');
    setAddEmployeeModalOpen(true);
  };

  const handleAddEmployeeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = addEmployeeEmail.trim().toLowerCase();
    if (!email) {
      setAddEmployeeError('Email is required.');
      return;
    }
    /** Match server `isPlausibleInviteEmail` — TFRI may invite any domain. */
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(email)) {
      setAddEmployeeError('Please enter a valid email address.');
      return;
    }
    if (!user?.id) return;
    setAddEmployeeError('');
    setAddEmployeeSuccess('');
    setAddEmployeeSending(true);
    try {
      const data = await inviteEmployee(email, user.id);
      setAddEmployeeSuccess(data.message ?? 'Invite sent.');
      setAddEmployeeEmail('');
      setTimeout(() => {
        setAddEmployeeSuccess('');
        setAddEmployeeModalOpen(false);
      }, 3000);
    } catch (err) {
      setAddEmployeeError(err instanceof Error ? err.message : 'Failed to send invite.');
    } finally {
      setAddEmployeeSending(false);
    }
  };

  if (!user) return null;
  if (!isAdmin) return null;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div>
            <PageHeader title="Employees" subtitle="View and manage platform users and their roles. Only TFRI can change roles or deactivate accounts." />
          </div>
          <div className="page-content">

            {deactivateError && !deactivateTarget && (
              <p className="create-order-error" role="alert" style={{ marginBottom: '0.75rem' }}>
                {deactivateError}
              </p>
            )}
            {loading ? (
              <p className="page-subtitle">Loading…</p>
            ) : (
              <>
                <div className="page-toolbar">
                  <input
                    type="text"
                    className="page-toolbar-search"
                    placeholder="Search by name, email, role, or status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search employees"
                  />
                  <button
                    type="button"
                    className="page-toolbar-btn"
                    onClick={handleAddEmployeeOpen}
                  >
                    + Add Employee
                  </button>
                </div>
                <div className="employees-table-wrapper">
                  <table className="employees-table">
                    <thead>
                      <tr>
                        <SortableTh label="Username" columnKey="username" sortKey={employeeSort.sortKey} sortDir={employeeSort.sortDir} onSort={employeeSort.toggleSort} />
                        <SortableTh label="Email" columnKey="email" sortKey={employeeSort.sortKey} sortDir={employeeSort.sortDir} onSort={employeeSort.toggleSort} />
                        <SortableTh label="Roles" columnKey="roles" sortKey={employeeSort.sortKey} sortDir={employeeSort.sortDir} onSort={employeeSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={employeeSort.sortKey} sortDir={employeeSort.sortDir} onSort={employeeSort.toggleSort} />
                        <StaticTh>Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFilteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="employees-empty">
                            {users.length === 0 ? 'No users found.' : 'No employees match your search.'}
                          </td>
                        </tr>
                      ) : (
                        sortedFilteredUsers.map((u) => {
                          const tasks = tasksForUser(u.id);
                          const isExpanded = expandedUserId === u.id;
                          const deactivated = isUserDeactivated(u);
                          const isSelf = user.id === u.id;
                          return (
                            <React.Fragment key={u.id}>
                              <tr className={deactivated ? 'employees-row-deactivated' : undefined}>
                                <td>{u.username}</td>
                                <td>{u.email}</td>
                                <td>
                                  <div className="employees-roles-pills">
                                    {(getUserRoles(u).length === 0 ? ['—'] : getUserRoles(u)).map((r) => (
                                      <span
                                        key={r}
                                        className="employees-pill"
                                        style={TAG_PILL_COLORS[r] ? { backgroundColor: TAG_PILL_COLORS[r] } : undefined}
                                      >
                                        {ROLE_LABELS[r] ?? r}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <span className={`employees-status-pill${deactivated ? ' employees-status-pill--deactivated' : ''}`}>
                                    {deactivated ? 'Deactivated' : 'Active'}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="update-button employees-edit-btn"
                                    onClick={() => openEdit(u)}
                                  >
                                    Edit Roles
                                  </button>
                                  <button
                                    type="button"
                                    className="update-button employees-view-tasks-btn"
                                    onClick={() => toggleTasks(u.id)}
                                    title={isExpanded ? 'Hide tasks' : 'View assigned tasks'}
                                    aria-expanded={isExpanded}
                                  >
                                    {isExpanded ? 'Hide tasks' : 'View tasks'}
                                  </button>
                                  {deactivated ? (
                                    <button
                                      type="button"
                                      className="update-button employees-reactivate-btn"
                                      onClick={() => void handleReactivate(u)}
                                    >
                                      Reactivate
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="employees-deactivate-btn"
                                      onClick={() => openDeactivate(u)}
                                      disabled={isSelf}
                                      title={isSelf ? 'You cannot deactivate your own account.' : 'Deactivate this account'}
                                    >
                                      Deactivate
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="employees-tasks-row">
                                  <td colSpan={5} className="employees-tasks-cell">
                                    <div className="employees-tasks-table-wrapper">
                                      <UserTasksTable tasks={tasks} />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {editUserId !== null && (
              <div className="employees-modal-overlay" onClick={closeEdit}>
                <div className="employees-modal" onClick={(e) => e.stopPropagation()}>
                  <h3 className="section-title">Edit roles</h3>
                  <p className="page-subtitle" style={{ marginBottom: '1rem' }}>
                    {users.find((u) => u.id === editUserId)?.username ?? 'User'} — select all roles that apply. TFRI users can grant the TFRI role.
                  </p>
                  <TagSelector
                    options={ROLE_OPTIONS}
                    labels={ROLE_LABELS}
                    selected={editRoles}
                    onChange={handleRolesChange}
                    colors={TAG_PILL_COLORS}
                  />
                  {saveError && (
                    <p className="create-order-error" role="alert" style={{ marginTop: '0.75rem' }}>
                      {saveError}
                    </p>
                  )}
                  <div className="form-actions" style={{ marginTop: '1rem' }}>
                    <button type="button" className="cancel-button" onClick={closeEdit}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="update-button"
                      disabled={saving || editRoles.length === 0}
                      onClick={handleSaveRoles}
                    >
                      {saving ? 'Saving…' : 'Save roles'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <Modal
        isOpen={addEmployeeModalOpen}
        onClose={() => setAddEmployeeModalOpen(false)}
        title="Add employee"
        narrow
      >
        <form onSubmit={handleAddEmployeeSubmit}>
          <div className="modal-body">
            <p className="page-subtitle" style={{ marginBottom: '1rem' }}>
              Enter their email address. They will receive a link to create a password and sign in (company or external addresses are allowed).
            </p>
            <div className="form-group">
              <label htmlFor="add-employee-email" className="form-label">Email</label>
              <input
                id="add-employee-email"
                type="email"
                className="form-input"
                value={addEmployeeEmail}
                onChange={(e) => setAddEmployeeEmail(e.target.value)}
                placeholder="name@example.com"
                required
              />
            </div>
            {addEmployeeError && (
              <p className="create-order-error" role="alert" style={{ marginTop: '0.5rem' }}>{addEmployeeError}</p>
            )}
            {addEmployeeSuccess && (
              <p className="settings-success" role="alert" style={{ marginTop: '0.5rem' }}>{addEmployeeSuccess}</p>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setAddEmployeeModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="update-button" disabled={addEmployeeSending}>
              {addEmployeeSending ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={warnSelfAdminRevoke}
        onClose={cancelAdminRevoke}
        title="Remove your own TFRI role?"
        narrow
      >
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            You are deselecting the <strong>TFRI</strong> role from your own account. Once saved, you will lose access to this page and will need another TFRI user to restore it.
          </p>
          <p style={{ marginBottom: '0', fontWeight: 600, color: '#b45309' }}>
            Are you sure you want to remove it?
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={cancelAdminRevoke}>
            Keep TFRI role
          </button>
          <button
            type="button"
            className="employees-revoke-admin-btn"
            onClick={confirmAdminRevoke}
          >
            Yes, remove it
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={deactivateTarget != null}
        onClose={closeDeactivate}
        title="Deactivate this account?"
        narrow
      >
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            <strong>{deactivateTarget?.username ?? 'This employee'}</strong> will be signed out immediately
            and will not be able to log in until you reactivate the account.
          </p>
          <p style={{ marginBottom: '0' }}>
            They will see a message on the sign-in page asking them to contact an admin.
          </p>
          {deactivateError && (
            <p className="create-order-error" role="alert" style={{ marginTop: '0.75rem' }}>
              {deactivateError}
            </p>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={closeDeactivate} disabled={deactivating}>
            Cancel
          </button>
          <button
            type="button"
            className="employees-deactivate-btn"
            onClick={() => void handleConfirmDeactivate()}
            disabled={deactivating}
          >
            {deactivating ? 'Deactivating…' : 'Deactivate account'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Employees;
